/**
 * The garage's working week, as published by `GET .../branch-schedule`.
 *
 * A port of the app's own helper (lib/core/helpers/branch_schedule.dart), kept
 * deliberately line-for-line with it: the web and the app must offer the same
 * delivery slots, or a garage is told two different things about when it is
 * open. The backend refuses a slot outside this window anyway — this is what
 * stops the user reaching one in the first place.
 *
 * Weekdays are ISO throughout: Monday is 1 and Sunday is 7, which is what the
 * api publishes and what Dart's `DateTime.weekday` gives. JavaScript's
 * `getDay()` is Sunday-based, so it is converted on the way in and never used
 * raw.
 */

const MINUTES_PER_DAY = 24 * 60;

/** Minutes since midnight for "HH:MM"; NaN-safe, like the app's helper. */
const minutesOf = (time) => {
  const parts = `${time ?? ""}`.split(":");
  const h = Number.parseInt(parts[0], 10);
  const m = Number.parseInt(parts[1], 10);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

/** The ISO weekday (Mon = 1 … Sun = 7) of a JS date. */
export const isoWeekday = (date) => ((date.getDay() + 6) % 7) + 1;

/**
 * Null when the branch has no usable appointment settings — nothing to hold
 * the choice inside, so the caller falls back to a plain picker rather than
 * offering an empty list of slots.
 */
export const parseSchedule = (raw) => {
  if (!raw) return null;
  const days = Array.isArray(raw.workingWeekdays)
    ? raw.workingWeekdays.map(Number).filter(Number.isFinite)
    : null;
  const hours = `${raw.workingHours ?? ""}`;
  if (!days || days.length === 0 || hours.indexOf("-") === -1) return null;

  const [open, close] = hours.split("-");
  const [breakFrom, breakTo] = `${raw.breakTime ?? ""}`.split("-");
  return {
    workingWeekdays: days,
    openMinutes: minutesOf(open),
    closeMinutes: minutesOf(close),
    breakFromMinutes: minutesOf(breakFrom),
    breakToMinutes: minutesOf(breakTo),
    workingHours: hours,
    breakTime: `${raw.breakTime ?? ""}`,
  };
};

export const isWorkingDay = (schedule, date) =>
  !schedule || schedule.workingWeekdays.indexOf(isoWeekday(date)) !== -1;

/**
 * True when the clock time falls inside opening hours. Handles a garage that
 * closes after midnight, where the range wraps.
 */
export const isWithinHours = (schedule, minutes) =>
  schedule.openMinutes < schedule.closeMinutes
    ? minutes >= schedule.openMinutes && minutes <= schedule.closeMinutes
    : minutes >= schedule.openMinutes || minutes <= schedule.closeMinutes;

export const isDuringBreak = (schedule, minutes) =>
  schedule.breakToMinutes > schedule.breakFromMinutes &&
  minutes > schedule.breakFromMinutes &&
  minutes < schedule.breakToMinutes;

/**
 * Every deliverable time on a working day, as minutes from that day's
 * midnight, stepping from opening to closing time inclusive — closing time
 * itself is a valid promise, matching `isWithinHours`.
 *
 * A garage that closes after midnight wraps, so the tail slots run past 1440:
 * callers add them to the day as a plain offset, which rolls the delivery into
 * the following morning on its own.
 */
export const deliverySlots = (schedule, stepMinutes = 30) => {
  const end =
    schedule.closeMinutes > schedule.openMinutes
      ? schedule.closeMinutes
      : schedule.closeMinutes + MINUTES_PER_DAY;
  const out = [];
  for (let m = schedule.openMinutes; m <= end; m += stepMinutes) out.push(m);
  return out;
};

/**
 * The first working day on or after `from` — the day the calendar should open
 * on, since it cannot land on a greyed-out one.
 */
export const firstWorkingDayFrom = (schedule, from) => {
  if (!schedule) return from;
  const day = new Date(from.getTime());
  for (let i = 0; i < 14; i++) {
    if (isWorkingDay(schedule, day)) return day;
    day.setDate(day.getDate() + 1);
  }
  return from;
};

/** "07:30", from minutes that may have wrapped past midnight. */
export const formatSlot = (minutes) => {
  const within = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) %
    MINUTES_PER_DAY;
  const two = (value) => String(value).padStart(2, "0");
  return `${two(Math.floor(within / 60))}:${two(within % 60)}`;
};

/** The moment a slot falls on, given the day it was picked for. */
export const slotDate = (day, minutes) => {
  const at = new Date(day.getFullYear(), day.getMonth(), day.getDate());
  at.setMinutes(at.getMinutes() + minutes);
  return at;
};
